import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack
} from '@mui/material';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { useGarage } from '../../contexts/GarageContext';
import AppointmentCard from './AppointmentCard';
import AppointmentSummaryModal from '../garage-admin/AppointmentSummaryModal';
import NewAppointmentModal from '../garage-admin/NewAppointmentModal';
import CategoryLegend from './CategoryLegend';
import { CATEGORY_CONFIG } from '../../utils/categoryConstants';

const DayView = ({ 
  currentDate, 
  appointments,
  getCategoryCounts,
  availableProducts,
  availableAddons,
  isWithinBusinessHours,
  isBusinessDay,
  onRefreshCalendar
}) => {
  const theme = useTheme();
  const { isDark } = useAppTheme();
  const { selectedGarage } = useGarage();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  // Local state for appointment details modal
  const [appointmentDetailsOpen, setAppointmentDetailsOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // Local state for new appointment modal
  const [newAppointmentOpen, setNewAppointmentOpen] = useState(false);
  const [selectedSlotDate, setSelectedSlotDate] = useState(null);
  const [selectedSlotTime, setSelectedSlotTime] = useState(null);
  
  // State for current time to trigger re-renders
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Handle appointment card click - open appointment details
  const handleAppointmentClick = (appointment, event) => {
    event?.stopPropagation();
    setSelectedAppointment(appointment.originalBooking);
    setAppointmentDetailsOpen(true);
  };

  // Handle empty slot click - open new appointment modal
  const handleSlotClick = (date, time, event) => {
    event?.stopPropagation();
    
    // Check if the time slot is in the future
    const now = new Date();
    const slotDateTime = new Date(date);
    const [hours, minutes] = time.split(':').map(Number);
    slotDateTime.setHours(hours, minutes, 0, 0);
    
    // Only allow future time slots
    if (slotDateTime <= now) {
      return; // Don't open modal for past time slots
    }

    // Check if date is a business day
    if (isBusinessDay && !isBusinessDay(date)) {
      return; // Don't open modal for closed days
    }

    // Check if time is within business hours
    if (isWithinBusinessHours && !isWithinBusinessHours(date, time)) {
      return; // Don't open modal for closed hours
    }
    
    setSelectedSlotDate(date);
    setSelectedSlotTime(time);
    setNewAppointmentOpen(true);
  };

  // Handle appointment modal close
  const handleAppointmentDetailsClose = () => {
    setAppointmentDetailsOpen(false);
    setSelectedAppointment(null);
  };

  // Handle new appointment modal close
  const handleNewAppointmentClose = () => {
    setNewAppointmentOpen(false);
    setSelectedSlotDate(null);
    setSelectedSlotTime(null);
  };

  // Handle appointment creation success
  const handleAppointmentSuccess = () => {
    handleNewAppointmentClose();
    // Refresh calendar to show new appointment
    if (onRefreshCalendar) {
      onRefreshCalendar();
    }
  };

  // Handle appointment data change
  const handleAppointmentDataChange = () => {
    // Refresh calendar to show updated appointment
    if (onRefreshCalendar) {
      onRefreshCalendar();
    }
  };

  // Generate time slots (8 AM to 6 PM)
  const getTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour < 18; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    return slots;
  };

  // Get appointments for the current date
  const getDayAppointments = () => {
    const dateStr = currentDate.getFullYear() + '-' + String(currentDate.getMonth() + 1).padStart(2, '0') + '-' + String(currentDate.getDate()).padStart(2, '0');
    return appointments
      .filter(apt => apt.date === dateStr)
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  // Format time for display
  const formatTimeDisplay = (time) => {
    const [hours, minutes] = time.split(':');
    const hour12 = parseInt(hours) > 12 ? parseInt(hours) - 12 : parseInt(hours) === 0 ? 12 : parseInt(hours);
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Check if time slot has an appointment
  const getAppointmentForSlot = (slotTime) => {
    return dayAppointments.find(apt => {
      if (!apt.startTime) return false;
      const aptStartTime = apt.startTime;
      const aptEndTime = apt.endTime || apt.startTime;
      
      // Check if slot time falls within appointment duration
      return slotTime >= aptStartTime && slotTime < aptEndTime;
    });
  };

  // Check if two appointments have any time conflict - if so, display side-by-side
  const appointmentsOverlap = (apt1, apt2) => {
    if (!apt1.startTime || !apt1.endTime || !apt2.startTime || !apt2.endTime) return false;
    
    const apt1Start = apt1.startTime;
    const apt1End = apt1.endTime;
    const apt2Start = apt2.startTime;
    const apt2End = apt2.endTime;
    
    return apt1Start < apt2End && apt2Start < apt1End;
  };

  // Calculate overlapping groups for appointments
  const getOverlappingGroups = (appointments) => {
    const groups = [];
    const processed = new Set();
    
    appointments.forEach((appointment, index) => {
      if (processed.has(index)) return;
      
      const group = [appointment];
      processed.add(index);
      
      // Find all overlapping appointments
      appointments.forEach((otherAppointment, otherIndex) => {
        if (otherIndex === index || processed.has(otherIndex)) return;
        
        const overlapsWithGroup = group.some(groupApp => 
          appointmentsOverlap(groupApp, otherAppointment)
        );
        
        if (overlapsWithGroup) {
          group.push(otherAppointment);
          processed.add(otherIndex);
        }
      });
      
      groups.push(group);
    });
    
    return groups;
  };

  // Calculate appointment position, height, and width for overlapping
  const getAppointmentStyle = (appointment, dayAppointments) => {
    const startTime = appointment.startTime;
    const endTime = appointment.endTime;
    
    if (!startTime || !endTime) return null;
    
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    
    // 8 AM = 480 minutes, 6 PM = 1080 minutes
    const dayStartMinutes = 8 * 60;
    const dayEndMinutes = 18 * 60;
    
    if (startTotalMinutes < dayStartMinutes || startTotalMinutes >= dayEndMinutes) {
      return null; // Outside business hours
    }
    
    // Dynamic slot height based on screen size (matching actual slot heights)
    const slotHeight = isMdUp ? 55 : isSmUp ? 50 : 45;
    const minutesFromStart = startTotalMinutes - dayStartMinutes;
    const duration = Math.min(endTotalMinutes, dayEndMinutes) - startTotalMinutes;
    
    const top = (minutesFromStart / 30) * slotHeight;
    const height = Math.max((duration / 30) * slotHeight, slotHeight * 0.8);
    
    // Calculate overlapping layout
    const overlappingGroups = getOverlappingGroups(dayAppointments);
    const currentGroup = overlappingGroups.find(group => 
      group.some(apt => apt.id === appointment.id)
    );
    
    if (!currentGroup || currentGroup.length === 1) {
      // No overlap - full width
      return { 
        top, 
        height, 
        left: 8, 
        right: 8, 
        width: 'calc(100% - 16px)'
      };
    }
    
    // Multiple overlapping appointments
    const groupSize = currentGroup.length;
    const appointmentIndex = currentGroup.findIndex(apt => apt.id === appointment.id);
    
    // Calculate width and position for overlapping
    const availableWidth = 100; // percentage
    const appointmentWidth = availableWidth / groupSize;
    const leftOffset = appointmentIndex * appointmentWidth;
    
    return { 
      top, 
      height, 
      left: `${leftOffset}%`, 
      width: `${appointmentWidth - 1}%` // Small gap between overlapping events
    };
  };

  // Check if it's current time
  const isCurrentTime = (slotTime) => {
    const today = new Date();
    const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
    const currentDateStr = currentDate.getFullYear() + '-' + String(currentDate.getMonth() + 1).padStart(2, '0') + '-' + String(currentDate.getDate()).padStart(2, '0');
    
    if (todayStr !== currentDateStr) return false;
    
    const currentTimeStr = currentTime.toTimeString().substring(0, 5);
    return slotTime <= currentTimeStr && currentTimeStr < getNextSlotTime(slotTime);
  };

  // Get next slot time
  const getNextSlotTime = (currentSlot) => {
    const [hours, minutes] = currentSlot.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + 30;
    const nextHours = Math.floor(totalMinutes / 60);
    const nextMinutes = totalMinutes % 60;
    return `${nextHours.toString().padStart(2, '0')}:${nextMinutes.toString().padStart(2, '0')}`;
  };

  const timeSlots = getTimeSlots();
  const dayAppointments = getDayAppointments();
  const dateStr = currentDate.toLocaleDateString('en-GB', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Check if current date is today for today highlighting
  const isTodayView = (() => {
    const today = new Date();
    return currentDate.toDateString() === today.toDateString();
  })();

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      p: { xs: 1, sm: 1.5, md: 2 }
    }}>
      {/* Category Legend */}
      <CategoryLegend compact={isMobile} availableProducts={availableProducts} />

      {/* Main timeline */}
      <Card sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* Day header */}
        <Box sx={{
          p: { xs: 1.5, sm: 2, md: 2.5 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: isDark ? 'grey.800' : 'grey.50',
          // Professional amber styling for today highlighting
          ...(isTodayView && {
            background: isDark 
              ? `linear-gradient(135deg, rgba(255, 193, 7, 0.08) 0%, rgba(255, 160, 0, 0.06) 100%)`
              : `linear-gradient(135deg, rgba(255, 193, 7, 0.06) 0%, rgba(255, 160, 0, 0.04) 100%)`,
            borderLeft: `4px solid ${isDark ? '#ffb300' : '#ff8f00'}`,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark
                ? `linear-gradient(45deg, transparent 49%, rgba(255, 193, 7, 0.03) 50%, transparent 51%)`
                : `linear-gradient(45deg, transparent 49%, rgba(255, 152, 0, 0.025) 50%, transparent 51%)`,
              pointerEvents: 'none'
            }
          })
        }}>
          <Typography 
            variant="h5" 
            fontWeight="bold" 
            sx={{
              color: isTodayView 
                ? (isDark ? '#ffcc02' : '#f57c00')
                : 'text.primary',
              position: 'relative',
              zIndex: 1
            }}
          >
            {dateStr}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {dayAppointments.length} appointments
            </Typography>
            {getCategoryCounts && (() => {
              const counts = getCategoryCounts(dayAppointments);
              
              return Object.entries(counts)
                .filter(([_, count]) => count > 0)
                .map(([category, count]) => (
                  <Avatar
                    key={category}
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      bgcolor: CATEGORY_CONFIG[category].color,
                      color: 'white'
                    }}
                  >
                    {CATEGORY_CONFIG[category].letter}{count}
                  </Avatar>
                ));
            })()}
          </Box>
        </Box>

        {/* Timeline */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          position: 'relative',
          minHeight: 0 // Allow flex child to shrink
        }}>
          <Box sx={{ 
            display: 'flex',
            minHeight: '100%'
          }}>
            {/* Time column */}
            <Box sx={{ 
              width: 100,
              borderRight: '1px solid', 
              borderColor: 'divider',
              bgcolor: isDark ? 'grey.900' : 'grey.50'
            }}>
              {timeSlots.map((time, index) => {
                const isCurrentTimeSlot = isCurrentTime(time);
                
                // Check if this time slot is available (for styling consistency)
                const now = new Date();
                const slotDateTime = new Date(currentDate);
                const [hours, minutes] = time.split(':').map(Number);
                slotDateTime.setHours(hours, minutes, 0, 0);
                
                const isPastSlot = slotDateTime <= now;
                const isBusinessDayAvailable = !isBusinessDay || isBusinessDay(currentDate);
                const isWithinHours = !isWithinBusinessHours || isWithinBusinessHours(currentDate, time);
                const isSlotAvailable = !isPastSlot && isBusinessDayAvailable && isWithinHours;
                
                return (
                  <Box
                    key={time}
                    sx={{
                      height: { xs: 45, sm: 50, md: 55 },
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderBottom: index % 2 === 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      bgcolor: isCurrentTimeSlot 
                        ? 'primary.50' 
                        : !isSlotAvailable 
                          ? 'action.hover'
                          : 'transparent',
                      opacity: isSlotAvailable ? 1 : 0.9
                    }}
                  >
                    {index % 2 === 0 && (
                      <Typography 
                        variant="body2" 
                        color={isCurrentTimeSlot ? 'primary.main' : 'text.secondary'}
                        sx={{ 
                          fontSize: '0.8rem', 
                          fontWeight: isCurrentTimeSlot ? 'bold' : 500,
                          textAlign: 'center'
                        }}
                      >
                        {formatTimeDisplay(time)}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Appointments column */}
            <Box sx={{
              flex: 1,
              position: 'relative',
              minHeight: { 
                xs: timeSlots.length * 22.5, 
                sm: timeSlots.length * 25, 
                md: timeSlots.length * 27.5 
              }
            }}>
              {/* Time slot backgrounds */}
              {timeSlots.map((time, slotIndex) => {
                const isCurrentTimeSlot = isCurrentTime(time);
                
                // Check if this time slot is available
                const now = new Date();
                const slotDateTime = new Date(currentDate);
                const [hours, minutes] = time.split(':').map(Number);
                slotDateTime.setHours(hours, minutes, 0, 0);
                
                const isPastSlot = slotDateTime <= now;
                const isBusinessDayAvailable = !isBusinessDay || isBusinessDay(currentDate);
                const isWithinHours = !isWithinBusinessHours || isWithinBusinessHours(currentDate, time);
                const isSlotAvailable = !isPastSlot && isBusinessDayAvailable && isWithinHours;
                
                return (
                  <Box
                    key={time}
                    sx={{
                      height: { xs: 45, sm: 50, md: 55 },
                      borderBottom: slotIndex % 2 === 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      bgcolor: isCurrentTimeSlot 
                        ? 'primary.50' 
                        : !isSlotAvailable 
                          ? 'action.hover'
                          : 'transparent',
                      opacity: isSlotAvailable ? 1 : 0.9,
                      '&:hover': {
                        bgcolor: isSlotAvailable ? (
                          isCurrentTimeSlot ? 'primary.100' : 'action.hover'
                        ) : undefined
                      },
                      cursor: isSlotAvailable ? 'pointer' : 'not-allowed',
                      position: 'relative',
                      // Add visual indicator for unavailable slots
                      ...(!isSlotAvailable && {
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: isDark 
                            ? 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.02) 4px, rgba(255,255,255,0.02) 8px)'
                            : 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.02) 4px, rgba(0,0,0,0.02) 8px)',
                          pointerEvents: 'none'
                        }
                      })
                    }}
                    onClick={(e) => {
                      if (isSlotAvailable) {
                        handleSlotClick(currentDate, time, e);
                      }
                    }}
                  >
                    {isCurrentTimeSlot && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: '50%',
                          height: 2,
                          bgcolor: 'primary.main',
                          zIndex: 5,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: -6,
                            top: -4,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: 'primary.main'
                          }
                        }}
                      />
                    )}
                  </Box>
                );
              })}


              {/* Appointments overlay */}
              {dayAppointments.map((appointment) => {
                const style = getAppointmentStyle(appointment, dayAppointments);
                if (!style) return null;

                return (
                  <Box
                    key={appointment.id}
                    sx={{
                      position: 'absolute',
                      top: style.top,
                      left: style.left,
                      width: style.width,
                      height: style.height
                    }}
                  >
                    <AppointmentCard
                      appointment={appointment}
                      variant="detailed"
                      showTime={false}
                      height={style.height}
                      isOverlapping={style.left !== 8} // Indicate if this is an overlapping event
                      onClick={(e) => handleAppointmentClick(appointment, e)}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Appointment Details Modal */}
      <AppointmentSummaryModal
        open={appointmentDetailsOpen}
        onClose={handleAppointmentDetailsClose}
        appointment={selectedAppointment}
        onDataChange={handleAppointmentDataChange}
        availableProducts={availableProducts}
        availableAddons={availableAddons}
      />

      {/* New Appointment Modal */}
      <NewAppointmentModal
        open={newAppointmentOpen}
        onClose={handleNewAppointmentClose}
        garageId={selectedGarage?.id}
        onSuccess={handleAppointmentSuccess}
        onError={(error) => console.error('Appointment creation error:', error)}
        availableProducts={availableProducts}
        availableAddons={availableAddons}
        prefilledDate={selectedSlotDate}
        prefilledTime={selectedSlotTime}
      />
    </Box>
  );
};

export default DayView;